import React from "react";
import { Menu, MenuItem } from "@mui/material";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { useTaxDispatch, useTaxSelector } from "../../redux/general/reduxStore";
import { logoutRequest } from "../../backend/taxguru/client/jmf/coreAuthClient";
import { resetUserSlice } from "../../redux/userSlice";
import { AuthorizedEntityComponent, FilterField, getPersonName } from "@mm-products/java-multima-framework";
import ListItemIcon from "@mui/material/ListItemIcon";
import Logout from "@mui/icons-material/Logout";
import { listAccounts } from "../../backend/taxguru/client/jmf/coreAccountClient";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import InfoIcon from "@mui/icons-material/Info";
import { useNavigate } from "react-router-dom";
import TaxGuruIconButton from "../../components/button/TaxGuruIconButton";
import AccountAvatar from "../../modules/account/component/AccountAvatar";
import { useQuery } from "react-query";
import AvatarSkeleton from "../../components/form/AvatarSkeleton";
import { navigationLinks } from "../Navigation";
import CreditComponent from "../../modules/licence/component/CreditComponent";
import Box from "@mui/material/Box";
import LocalizationSwitcher from "../LocalizationSwitcher";
import useMenuAnchorState from "../../components/hook/useMenuAnchorState";
import useFeedbackRequests from "../../modules/feedback/function/hook/useFeedbackRequests";
import operator = FilterField.operator;

const findAccount = (username: string) =>
    listAccounts({
        filters: [
            {
                field: "username",
                value: username,
                operator: operator.EQ,
            },
        ],
    }).then(({ data }) => data.data?.[0]);

const CreditMenuItem = () => {
    const { credit } = useTaxSelector((state) => state.user);

    if (credit == null) {
        return null;
    }

    return (
        <Box sx={{ display: { xs: "block", lg: "none" } }}>
            <Divider />
            <MenuItem disableRipple disableTouchRipple dense>
                <CreditComponent small />
            </MenuItem>
        </Box>
    );
};

const LocalizationSwitcherMenuItem = () => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const onClick = (e: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(e.currentTarget);
    };
    return (
        <MenuItem onClick={onClick} dense>
            <LocalizationSwitcher setAnchorEl={setAnchorEl} anchorEl={anchorEl} />
        </MenuItem>
    );
};

const NavigationUserMenu = () => {
    const { anchorEl, open, onOpenMenu, onCloseMenu } = useMenuAnchorState();
    const { username } = useTaxSelector((state) => state.user);
    const { t } = useTranslation();
    const dispatch = useTaxDispatch();
    const navigate = useNavigate();
    const { createDirectUserFeedback } = useFeedbackRequests();

    const { data: account, isLoading } = useQuery({
        queryKey: ["userUsername", username],
        enabled: username !== undefined,
        queryFn: ({ queryKey }) => (queryKey[1] ? findAccount(queryKey[1]) : Promise.reject("Empty ID")),
        refetchOnWindowFocus: false,
    });

    const handleLogout = () =>
        logoutRequest()
            .then(() => navigate("/"))
            .then(() => dispatch(resetUserSlice()));

    const handleUserClick = () => {
        navigate(`${navigationLinks.ADMIN_USERS_LINK}/${account?.id}`);
        onCloseMenu();
    };

    return (
        <>
            <TaxGuruIconButton onClick={onOpenMenu} sx={{ p: 0 }}>
                <AvatarSkeleton loading={isLoading}>
                    <AccountAvatar account={account} />
                </AvatarSkeleton>
            </TaxGuruIconButton>
            <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                open={open}
                onClose={onCloseMenu}
            >
                <AuthorizedEntityComponent requiredPermissions={["read"]} unauthorizedMode="children-decide" entityPermissions={account}>
                    {(authorized) => (
                        <MenuItem dense onClick={() => authorized && handleUserClick()}>
                            <Stack>
                                <Typography variant="body1">{getPersonName(account?.person)}</Typography>
                                <Typography variant={account?.person ? "body2" : "body1"}>{username}</Typography>
                            </Stack>
                        </MenuItem>
                    )}
                </AuthorizedEntityComponent>
                <CreditMenuItem />
                <Divider />
                <LocalizationSwitcherMenuItem />
                <Divider />
                <MenuItem onClick={createDirectUserFeedback}>
                    <Typography>{t("feedback.self")}</Typography>
                </MenuItem>
                <Divider />
                <MenuItem
                    dense
                    onClick={() => {
                        navigate(navigationLinks.ABOUT_LINK);
                        onCloseMenu();
                    }}
                >
                    <ListItemIcon>
                        <InfoIcon fontSize="small" />
                    </ListItemIcon>
                    <Typography textAlign="center">{t("navigation.about")}</Typography>
                </MenuItem>
                <MenuItem dense onClick={handleLogout}>
                    <ListItemIcon>
                        <Logout fontSize="small" />
                    </ListItemIcon>
                    <Typography textAlign="center">{t("jmf.logout")}</Typography>
                </MenuItem>
            </Menu>
        </>
    );
};

export default NavigationUserMenu;
